import React, { useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import CommonButton from "../commonButton";
import CustomSelect from "../form/CustomSelect";
import Editor from "../form/CustomEditor";
import withModal from "../withModal";
import { useToast } from "../../context/TostContext";
import PreviewModal from "../modals/previewModal";
import { Delete02Icon } from "../../assets/icons";
import { Calendar } from "lucide-react";
import { FormLabel } from "../form/utils";

const visibilityOptions = [
  { value: "public", label: "Public" },
  { value: "connections", label: "Connections" },
  { value: "private", label: "Private" },
];

// Returns a datetime-local string rounded up to the next minute, at least 2 minutes in the future
const getMinScheduleTime = () => {
  const now = new Date(Date.now() + 2 * 60 * 1000); // minimum 2 minutes from now
  // Format as YYYY-MM-DDTHH:MM (datetime-local format)
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const PostModal = ({ onClose, onSubmit, post, isLoading, mediaType }) => {
  const [content, setContent] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [removedMediaIds, setRemovedMediaIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [currentMediaType, setCurrentMediaType] = useState("image");

  // Scheduling state
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");

  useEffect(() => {
    if (post) {
      setContent(post.content || "");
      setVisibility(post.visibility || "public");
      setRemovedMediaIds([]);
      setSelectedFiles([]);

      // Pre-fill scheduling if the post is already scheduled
      if (post.status === "scheduled" && post.scheduled_at) {
        setIsScheduled(true);
        // Convert ISO string to datetime-local format
        const d = new Date(post.scheduled_at);
        const pad = (n) => String(n).padStart(2, "0");
        setScheduledAt(
          `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
        );
      } else {
        setIsScheduled(false);
        setScheduledAt("");
      }

      if (post.media && post.media.length > 0) {
        setPreviews(post.media.map(m => ({
          id: m.id,
          url: m.file.url,
          mimetype: m.mimetype,
          isExisting: true
        })));
        const isVideo = post.media.some(m => m.mimetype?.startsWith("video/"));
        setCurrentMediaType(isVideo ? "video" : "image");
      } else {
        setPreviews([]);
        setCurrentMediaType(null);
      }
    } else {
      setContent("");
      setVisibility("public");
      setSelectedFiles([]);
      setPreviews([]);
      setRemovedMediaIds([]);
      setCurrentMediaType(mediaType);
      setIsScheduled(false);
      setScheduledAt("");
    }
    setIsSubmitting(false);
  }, [post, mediaType]);

  const handleFileSelect = (e, file) => {
    if (file) {
      setPreviews((prevPresets) => {
        if (prevPresets.length >= 5) {
          toast.error("Maximum 5 files allowed");
          return prevPresets;
        }

        setSelectedFiles((prevFiles) => [...prevFiles, file]);

        return [...prevPresets, {
          url: URL.createObjectURL(file),
          mimetype: file.type,
          isExisting: false,
          file: file
        }];
      });
    }
  };

  const handleRemovePreview = (index) => {
    const previewToRemove = previews[index];
    if (previewToRemove.isExisting) {
      setRemovedMediaIds([...removedMediaIds, previewToRemove.id]);
    } else {
      setSelectedFiles(selectedFiles.filter(f => f !== previewToRemove.file));
    }
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setPreviews(updatedPreviews);

    if (updatedPreviews.length === 0) {
      const hasOriginalMedia = post && post.media && post.media.length > 0;
      if (!hasOriginalMedia) {
        setCurrentMediaType(post ? null : mediaType);
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    const activePreviews = previews.filter(p => !removedMediaIds.includes(p.id));
    const plainTextContent = content.replace(/<[^>]*>?/gm, '').trim();

    if (!plainTextContent && selectedFiles.length === 0 && activePreviews.length === 0) {
      toast.error("Please provide either content or media for the post.");
      return;
    }

    // Validate scheduling
    if (isScheduled) {
      if (!scheduledAt) {
        toast.error("Please select a date and time for scheduling.");
        return;
      }
      const scheduleDate = new Date(scheduledAt);
      if (scheduleDate <= new Date()) {
        toast.error("Scheduled time must be in the future.");
        return;
      }
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("content", content);
    formData.append("visibility", visibility);

    // Append scheduled_at if scheduling
    if (isScheduled && scheduledAt) {
      formData.append("scheduled_at", new Date(scheduledAt).toISOString());
    }

    selectedFiles.forEach((file) => {
      formData.append("media", file);
    });

    if (removedMediaIds.length > 0) {
      removedMediaIds.forEach(id => {
        formData.append("removed_media_ids", id);
      });
    }

    onSubmit(formData);
  };

  const previewMediaArray = previews.map((prev, index) => ({
    url: prev.url,
    type: prev.mimetype?.startsWith("video/") ? "video" : "image",
    title: `Media ${index + 1}`
  }));

  // Only show scheduling toggle for new posts or posts that are currently scheduled
  const canSchedule = !post || post.status === "scheduled";

  return (
    <Form onSubmit={handleSubmit}>
      <Modal.Body className="grid gap-3">

       
          <Form.Group className="g-col-md-8 mb-3">
            <FormLabel labelName="Content" />
            <div style={{ marginBottom: "64px" }}>
              <Editor
                value={content}
                onChange={setContent}
                height="180px"
              />
            </div>
          </Form.Group>
        
        <div className="g-col-md-4">
          <CustomSelect
            label={{ name: "Visibility" }}
            name="visibility"
            value={visibility}
            options={visibilityOptions}
            onChange={(option) => setVisibility(option ? option.value : "public")}
            isClearable={false}
          />

          {/* Scheduling Toggle */}
          {canSchedule && (
            <Form.Group className="mt-3">
              <div
                className="d-flex align-items-center gap-2 mb-2"
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  setIsScheduled((prev) => !prev);
                  if (isScheduled) setScheduledAt("");
                }}
              >
                <Form.Check
                  type="switch"
                  id="schedule-toggle"
                  checked={isScheduled}
                  onChange={() => { }}
                  style={{ pointerEvents: "none" }}
                />
                <Form.Label
                  htmlFor="schedule-toggle"
                  className="mb-0 fw-medium d-flex align-items-center gap-1"
                  style={{ cursor: "pointer", fontSize: "14px" }}
                >
                  <Calendar size={14} />
                  Schedule Post
                </Form.Label>
              </div>

              {isScheduled && (
                <div>
                  <Form.Control
                    type="datetime-local"
                    value={scheduledAt}
                    min={getMinScheduleTime()}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    required={isScheduled}
                    style={{ fontSize: "13px" }}
                  />
                  <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
                    Post will be published automatically at this time.
                  </Form.Text>
                </div>
              )}
            </Form.Group>
          )}
        </div>

        <div className="mb-3 g-col-12">
          <Form.Label>
            Post Media (Max 5 {currentMediaType === "video" ? "Videos" : currentMediaType === "image" ? "Images" : "Files"})
          </Form.Label>
          <div className="d-flex flex-wrap gap-3 mb-2">
            {previews.map((prev, index) => (
              <div
                key={index}
                className="position-relative border rounded overflow-hidden"
                style={{ width: "120px", height: "120px" }}
              >
                {prev.mimetype?.startsWith("video/") ? (
                  <div
                    className="position-relative w-100 h-100"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setPreviewIndex(index);
                      setShowPreviewModal(true);
                    }}
                  >
                    <video
                      src={prev.url}
                      className="w-100 h-100 object-fit-cover"
                    />
                    <div
                      className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center bg-dark bg-opacity-75 rounded-circle shadow-sm"
                      style={{ width: "36px", height: "36px", zIndex: 1, pointerEvents: "none" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="#ffffff"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <img
                    src={prev.url}
                    alt="preview"
                    className="w-100 h-100 object-fit-cover"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setPreviewIndex(index);
                      setShowPreviewModal(true);
                    }}
                  />
                )}
                <button
                  type="button"
                  className="position-absolute top-0 end-0 bg-danger text-white border-0 d-flex align-items-center justify-content-center"
                  style={{
                    width: "28px",
                    height: "28px",
                    borderBottomLeftRadius: "6px",
                    zIndex: 2
                  }}
                  onClick={() => handleRemovePreview(index)}
                >
                  <Delete02Icon size={14} />
                </button>
              </div>
            ))}

            {previews.length < 5 && (
              currentMediaType === "video" ? (
                <div
                  className="d-flex align-items-center justify-content-center border rounded cursor-pointer bg-light"
                  style={{ width: "120px", height: "120px", border: "2px dashed #ddd" }}
                  onClick={() => {
                    const input = document.getElementById('post-media-input');
                    input.accept = "video/*";
                    input.click();
                  }}
                >
                  <div className="text-center text-muted">
                    <div style={{ fontSize: "24px" }}>+</div>
                    <div style={{ fontSize: "12px" }}>Add Video</div>
                  </div>
                </div>
              ) : currentMediaType === "image" ? (
                <div
                  className="d-flex align-items-center justify-content-center border rounded cursor-pointer bg-light"
                  style={{ width: "120px", height: "120px", border: "2px dashed #ddd" }}
                  onClick={() => {
                    const input = document.getElementById('post-media-input');
                    input.accept = "image/*";
                    input.click();
                  }}
                >
                  <div className="text-center text-muted">
                    <div style={{ fontSize: "24px" }}>+</div>
                    <div style={{ fontSize: "12px" }}>Add Image</div>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    className="d-flex align-items-center justify-content-center border rounded cursor-pointer bg-light me-3"
                    style={{ width: "120px", height: "120px", border: "2px dashed #ddd" }}
                    onClick={() => {
                      const input = document.getElementById('post-media-input');
                      input.accept = "image/*";
                      input.click();
                    }}
                  >
                    <div className="text-center text-muted">
                      <div style={{ fontSize: "24px" }}>+</div>
                      <div style={{ fontSize: "12px" }}>Add Image</div>
                    </div>
                  </div>
                  <div
                    className="d-flex align-items-center justify-content-center border rounded cursor-pointer bg-light"
                    style={{ width: "120px", height: "120px", border: "2px dashed #ddd" }}
                    onClick={() => {
                      const input = document.getElementById('post-media-input');
                      input.accept = "video/*";
                      input.click();
                    }}
                  >
                    <div className="text-center text-muted">
                      <div style={{ fontSize: "24px" }}>+</div>
                      <div style={{ fontSize: "12px" }}>Add Video</div>
                    </div>
                  </div>
                </>
              )
            )}
          </div>

          <input
            id="post-media-input"
            type="file"
            className="d-none"
            multiple
            onChange={(e) => {
              const files = Array.from(e.target.files);
              if (files.length === 0) return;

              const isVideoUpload = files.some(file => file.type.startsWith("video/"));
              const isImageUpload = files.some(file => file.type.startsWith("image/"));

              if (isVideoUpload && isImageUpload) {
                toast.error("You cannot select both images and videos in a single post.");
                e.target.value = '';
                return;
              }

              const activeType = isVideoUpload ? "video" : "image";
              let targetType = currentMediaType;

              if (!targetType) {
                targetType = activeType;
                setCurrentMediaType(activeType);
              } else if (targetType !== activeType) {
                toast.error(`A post cannot contain both images and videos. Please upload only ${targetType}s.`);
                e.target.value = '';
                return;
              }

              const currentCount = previews.length;
              if (currentCount + files.length > 5) {
                toast.error("You cannot select more than 5 files in total.");
                e.target.value = '';
                return;
              }

              files.forEach(file => handleFileSelect(null, file));
              e.target.value = '';
            }}
          />

          <Form.Text className="text-muted d-block mt-2">
            {currentMediaType === "video"
              ? "You can upload up to 5 videos. Media is optional."
              : currentMediaType === "image"
                ? "You can upload up to 5 images. Media is optional."
                : "You can upload up to 5 images or videos. Mixing is not allowed. Media is optional."}
          </Form.Text>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <CommonButton
          variant="outline"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
          disabled={isLoading || isSubmitting}
          title="Cancel"
        />
        <CommonButton
          variant="primary"
          type="submit"
          isLoading={isLoading || isSubmitting}
          disabled={isLoading || isSubmitting}
          title={
            isScheduled
              ? "Schedule Post"
              : post
                ? "Update Post"
                : "Publish Now"
          }
        />
      </Modal.Footer>
      <PreviewModal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        previewMedia={previewMediaArray}
        initialIndex={previewIndex}
      />
    </Form>
  );
};

export default withModal(PostModal);
